import React, { useEffect, useState } from "react";
import { Col, Container, Row } from "reactstrap";
import ParticlesAuth from "../AuthenticationInner/ParticlesAuth";

import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { useFormik } from "formik";

import AuthLogo from "../../Components/Auth/AuthLogo";
import AuthCard from "../../Components/Auth/AuthCard";
import AuthContainer from "../../Components/Auth/AuthContainer";
import CustomInput from "../../Components/Custom/CustomInput";
import CustomButton from "../../Components/Custom/CustomButton";
import { GlobalImage } from "config/image";
import { SketchPicker } from "react-color";
import BrandColorModal from "Components/Modals/BrandColorModal";
import { useAppDispatch, useAppSelector } from "Components/Hooks/hooks";
import { compressImage } from "utils/compressImage";
import { redirectPage } from "utils/redirectPage";
import { updateUserPayload } from "slices/auth/reducer";
import {
  VALIDATION_LIMITS,
  VALIDATION_MESSAGES,
  VALIDATION_PATTERNS,
  createNameValidation,
} from "Components/constants/validation";
import { ErrorToast } from "utils/toast";

const BusinessProfileSetup = () => {
  const [color, setColor] = useState<string>("#38b6ff");
  const [open, setOpen] = useState<boolean>(false);
  const [openInfoModal, setOpenInfoModal] = useState<boolean>(false);
  const [isOn, setIsOn] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { isLoading } = useAppSelector((state) => state?.auth);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedImage, setSelectedImage] = useState<string>(
    GlobalImage?.businessImagePlaceholder,
  );
  const user = useAppSelector((state) => state.auth.user);

  const configData = useAppSelector((state) => state.config.configData);

  const handleImageUpload = async (e: any) => {
    const file = e.target.files[0];
    if (!file) {
      return;
    }
    // Validate file type - only allow JPEG and PNG
    const allowedTypes = ["image/jpeg", "image/png", "image/jpg"];
    const fileExtension = file.name.split(".").pop()?.toLowerCase();
    const allowedExtensions = ["jpg", "jpeg", "png"];

    if (
      !allowedTypes.includes(file.type) &&
      !allowedExtensions.includes(fileExtension || "")
    ) {
      e.target.value = ""; // Reset the input
      ErrorToast("Only JPEG and PNG images are allowed");
      return;
    }

    const compressed = await compressImage(file);
    setSelectedFile(compressed);
    console.log(compressed);

    const previewUrl = URL.createObjectURL(compressed);

    setSelectedImage(previewUrl);
  };

  useEffect(() => {
    window.history.pushState(null, "", window.location.href);

    const onBack = () => {
      window.history.pushState(null, "", window.location.href);
    };

    window.addEventListener("popstate", onBack);
    return () => window.removeEventListener("popstate", onBack);
  }, []);

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;

    // Remove all non-digit characters except + and ()
    const cleanValue = value.replace(/[^\d]/g, "");

    // Build the formatted value
    let formattedValue = "";
    if (cleanValue.length > 0) {
      // Add country code
      const countryCode = cleanValue.startsWith("44")
        ? cleanValue.substring(0, 2)
        : "44";
      const phoneNumber = cleanValue.startsWith("44")
        ? cleanValue.substring(2)
        : cleanValue;

      // Format as (+44) XXXXXXXXXX for display (with space)
      formattedValue = `(+${countryCode}) ${phoneNumber}`;
    }

    // Update formik with formatted value for display
    validation.setFieldValue("phone", formattedValue);
  };

  const validation: any = useFormik({
    enableReinitialize: true,
    initialValues: {
      businessName: "",
      phone: "",
      trade: "",
      vatNumber: "",
    },
    validationSchema: Yup.object({
      businessName: Yup.string()
        .trim()
        .required(VALIDATION_MESSAGES.REQUIRED("your business name"))
        .min(
          VALIDATION_LIMITS.BUSINESS_NAME.MIN,
          VALIDATION_MESSAGES.MIN_LENGTH(
            "Business Name",
            VALIDATION_LIMITS.BUSINESS_NAME.MIN,
          ),
        )
        .max(
          VALIDATION_LIMITS.BUSINESS_NAME.MAX,
          VALIDATION_MESSAGES.MAX_LENGTH(
            "Business Name",
            VALIDATION_LIMITS.BUSINESS_NAME.MAX,
          ),
        )
        .test(
          "valid-business-name",
          createNameValidation("Business Name").message,
          createNameValidation("Business Name").test,
        ),

      phone: Yup.string()
        .trim()
        .required(VALIDATION_MESSAGES.REQUIRED("your phone number"))
        .matches(VALIDATION_PATTERNS.PHONE, VALIDATION_MESSAGES.INVALID_PHONE),
      trade: Yup.string().required("Please Select Your Trade"),
      vatNumber: Yup.string().when([], {
        is: () => isOn,
        then: (schema) =>
          schema
            .trim()
            .required(VALIDATION_MESSAGES.REQUIRED("your VAT number"))
            .matches(
              VALIDATION_PATTERNS.VAT_NUMBER,
              "VAT Number must contain only letters and numbers",
            )
            .max(
              VALIDATION_LIMITS.VAT_NUMBER.MAX,
              VALIDATION_MESSAGES.MAX_LENGTH(
                "VAT Number",
                VALIDATION_LIMITS.VAT_NUMBER.MAX,
              ),
            ),
        otherwise: (schema) => schema.notRequired(),
      }),
    }),
    onSubmit: (values) => {
      // if (!selectedFile) {
      //   ErrorHandler("Please upload your business logo to continue.");
      //   return;
      // }
      dispatch(
        updateUserPayload({
          is_businessSetUpCompleted: true,
        }),
      );

      // Clean phone number for backend (remove parentheses and spaces, keep +44)
      const cleanPhone = values.phone.replace(/[()]/g, "").replace(/\s+/g, "");

      setTimeout(() => {
        const route = redirectPage(user, false);
        if (route) {
          navigate("/business-address", {
            state: {
              ...values,
              businessName: values.businessName?.trim(),
              vatNumber: values.vatNumber?.trim(),
              phone: cleanPhone,
              brand_color: color,
              selectedFile,
            },
          });
        } else {
          navigate("/dashboard", { replace: true });
        }
      }, 0);
      // navigate("/business-address");
    },
  });

  const handleColorChange = (newColor: any) => {
    setColor(newColor.hex);
    // setOpen(false);
  };

  const handleOpenInfoModal = () => {
    setOpenInfoModal((prev) => !prev);
  };

  const handleColorPicker = () => {
    setOpen((prev) => !prev);
  };

  const handleToggle = () => {
    setIsOn((prev) => !prev);
  };

  const defaultOptions: any = configData?.vertical_markets.map((vm) => {
    return { label: vm.title, value: vm.id };
  });

  document.title = "Business Profile Setup | EaziQuote";
  return (
    <React.Fragment>
      <ParticlesAuth>
        <AuthContainer>
          <Container>
            <Row>
              <Col lg={12}>
                <AuthLogo />
              </Col>
            </Row>

            <Row className="auth-container">
              <Col md={8} lg={6} xl={5}>
                <AuthCard>
                  <div className="text-center mt-2">
                    <h5 className="text-black2D font-primary fw-semibold fs-24">
                      Business Profile Setup
                    </h5>
                  </div>

                  <div className="profile-img-container">
                    <div className="profile-img-wrapper position-relative d-inline-block">
                      <img
                        src={selectedImage}
                        alt="Profile"
                        className="rounded-circle profile-img"
                        width={100}
                        height={100}
                        style={{ objectFit: "cover" }}
                      />
                      <label className="edit-profile-btn position-absolute bg-primary btn-press-effect">
                        <i
                          className="ri-add-line text-white"
                          style={{ fontSize: 20, marginTop: 2 }}
                        />

                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          style={{ display: "none" }}
                        />
                      </label>
                    </div>
                  </div>

                  <p className="font-primary fs-14 text-black2D fw-normal text-center">
                    Your logo will appear on quotes, invoices, and client
                    emails.
                  </p>

                  <div className="d-flex gap-1 align-content-center mb-1">
                    <h2 className="font-primary fs-14 text-black2D fw-normal d-flex align-self-center">
                      Brand Colour
                    </h2>

                    <i
                      className="ri-error-warning-line text-black2D btn-press-effect"
                      style={{ fontSize: 18, marginTop: -4 }}
                      onClick={handleOpenInfoModal}
                    />
                  </div>

                  <>
                    <div
                      className="brand-color-container"
                      onClick={handleColorPicker}
                      style={{ cursor: "pointer" }}
                    >
                      <div
                        className="brand-color-picker"
                        style={{ backgroundColor: color }}
                      />
                    </div>

                    {open && (
                      <div
                        className="color-picker-overlay"
                        onMouseDown={(e) => {
                          if (e.target === e.currentTarget) {
                            handleColorPicker();
                          }
                        }}
                      >
                        <div onClick={(e) => e.stopPropagation()}>
                          <SketchPicker
                            color={color}
                            onChange={handleColorChange}
                          />
                        </div>
                      </div>
                    )}
                  </>

                  <div className="dashed" />

                  <div className="p-2">
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        validation.handleSubmit();
                      }}
                    >
                      <CustomInput
                        label="Business Name"
                        placeholder="Enter business name"
                        name="businessName"
                        type="text"
                        value={validation.values.businessName}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        touched={validation.touched.businessName}
                        error={validation.errors.businessName}
                      />
                      <CustomInput
                        label="Business Phone Number"
                        placeholder="Enter phone number"
                        name="phone"
                        type="tel"
                        value={validation.values.phone}
                        onChange={handlePhoneChange}
                        onBlur={validation.handleBlur}
                        touched={validation.touched.phone}
                        error={validation.errors.phone}
                      />
                      <CustomInput
                        label="What trade do you do?"
                        placeholder="Select an option"
                        name="trade"
                        type="select"
                        value={validation.values.trade}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        touched={validation.touched.trade}
                        error={validation.errors.trade}
                        dropdownOptions={defaultOptions}
                      />
                      <div className="dashed" />

                      <div className="d-flex gap-2 align-content-center">
                        <p className="font-primary fs-14 text-black2D fw-normal">
                          Are you VAT registered?
                        </p>
                        <div
                          className={`toggle-switch ${isOn ? "on" : "off"}`}
                          onClick={handleToggle}
                        >
                          <span
                            className={`toggle-label ${isOn ? "on" : "off"}`}
                          >
                            {isOn ? "Yes" : "No"}
                          </span>
                          <div
                            className={`toggle-circle ${isOn ? "on" : "off"}`}
                          />
                        </div>
                      </div>

                      {isOn && (
                        <CustomInput
                          label="VAT Number"
                          placeholder="Enter your VAT number"
                          name="vatNumber"
                          type="text"
                          value={validation.values.vatNumber}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          touched={validation.touched.vatNumber}
                          error={validation.errors.vatNumber}
                        />
                      )}
                      <CustomButton
                        text="Continue"
                        type="submit"
                        loading={isLoading}
                        className="auth-Button"
                      />
                    </form>
                  </div>
                  <BrandColorModal
                    isOpen={openInfoModal}
                    onClose={handleOpenInfoModal}
                  />
                </AuthCard>
              </Col>
            </Row>
          </Container>
        </AuthContainer>
      </ParticlesAuth>
    </React.Fragment>
  );
};

export default BusinessProfileSetup;
